// ENG-3012 TIFF deferral: both chart endpoints return a `warnings` array
// beside their success payload. Partial success (visible TIFF attachments
// deliberately excluded from the filing) must never look like ordinary
// success, so every entry path that files a document routes its response
// through here instead of showing only its usual success toast.
//
// "Every entry path" means all of them, not just Send to Chart: the reply merge
// runs for any Reply parent BEFORE the legacy endpoint branches on upload_type,
// so chart_creation, chart_and_task, upload_and_task and task_creation all come
// back carrying the warning. Current callers -- chartActions/helpers.js (direct
// auto-upload), modal/AutoUpload{,.legacy}.jsx, modal/CreateNewChart{,.legacy}.jsx,
// modal/CreateTask.jsx, containers/modals/DynamicEhrDrawer.jsx. A new caller that
// awaits a trigger* action without capturing its return silently drops the
// warning; capture the result.
import React from 'react';
import { Modal } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { captureAnalytics } from '../../actions/preload';

export const REPLY_TIFF_EXCLUDED_CODE = 'reply_tiff_excluded';

// Action-neutral on purpose: this reason is shown on Send to Chart, Create New
// Chart and Create Task alike, because the reply merge runs for every Reply
// parent before the endpoint branches on upload_type, so all three 422.
// Delivered through each item's antd Tooltip rather than as text inside the
// menu item: `.ant-menu-vertical .ant-menu-item` is `height: 40px;
// overflow: hidden`, so in-item text is clipped and invisible.
export const TIFF_ONLY_DISABLED_EXPLANATION = 'This reply only contains TIFF attachments, which cannot be '
  + 'filed to a chart. Convert the document to PDF and upload it separately.';

// The response contract is warnings: [] on ordinary success; anything else
// (older backend, non-chart endpoint) counts as no warnings.
export const extractReplyTiffWarnings = (result) => {
  const warnings = result?.data?.warnings;
  if (!Array.isArray(warnings)) {
    return [];
  }

  return warnings.filter((warning) => warning && warning.code === REPLY_TIFF_EXCLUDED_CODE);
};

const excludedCount = (warnings) => {
  const total = warnings.reduce((sum, warning) => sum + (Number(warning.excluded_count) || 0), 0);
  return total > 0 ? total : warnings.length;
};

// Acknowledged, accessible partial-success warning. antd's Modal.warning is
// the app's accessible dialog primitive (role="dialog", focus trap, keyboard
// acknowledgment); icon + title + body text carry the meaning, never color
// alone. It stays up until the user explicitly acknowledges it.
//
// Analytics carry ONLY the workflow slug and the excluded count -- never a
// filename, token, or patient field.
export const showReplyTiffWarningModal = ({ warnings, workflow, onAcknowledge }) => {
  if (!warnings || warnings.length === 0) {
    if (onAcknowledge) onAcknowledge();
    return false;
  }

  const count = excludedCount(warnings);
  const summary = count === 1
    ? 'Chart upload queued, but 1 TIFF attachment was not included.'
    : `Chart upload queued, but ${count} TIFF attachments were not included.`;
  const recovery = count === 1
    ? 'Upload it separately as a PDF if it must be added to the chart.'
    : 'Upload them separately as PDFs if they must be added to the chart.';

  captureAnalytics('reply_chart_upload_tiff_warning_shown', { workflow, excluded_count: count });

  Modal.warning({
    title: 'Chart upload queued — attachment not included',
    icon: <WarningOutlined />,
    content: `${summary} ${recovery}`,
    okText: 'I understand',
    keyboard: false,
    onOk: () => {
      captureAnalytics('reply_chart_upload_tiff_warning_acknowledged', { workflow, excluded_count: count });
      if (onAcknowledge) onAcknowledge();
    },
  });
  return true;
};
